import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectSelectedOperator,
  selectSelectedPlan,
} from "@/store/selectors/operator.selectors";
import {
  setSelectedOperator,
  setSelectedPlan,
} from "@/store/slices/operator.slice";

import { Operator } from "@/store/types/get-operators";
import { urlFor } from "@/lib/imageUrl";
import { PlanItem } from "./plan-item";
import { PhoneNumberSelection } from "./phone-number-selection";
import { t } from "i18next";

interface OperatorsButtonsProps {
  operators: Operator[];
}

export const OperatorsButtons = ({ operators }: OperatorsButtonsProps) => {
  const dispatch = useAppDispatch();
  const selectedOperator = useAppSelector(selectSelectedOperator);
  const selectedPlan = useAppSelector(selectSelectedPlan);
  // const phoneDetails = useAppSelector(selectPhoneDetails);

  const handleOperatorChange = (value: string | null) => {
    dispatch(setSelectedOperator(value));
    dispatch(setSelectedPlan(null));
  };

  const handlePlanSelect = (sku: string) => {
    dispatch(setSelectedPlan(selectedPlan === sku ? null : sku));
  };

  // Compute the "from" price for each operator and sort by lowest regular price
  const operatorsWithFromPrice = [...operators]
    .map((op) => {
      const lowestRegularPrice = Math.min(
        ...op.plans.map((plan) => plan.price) // Only consider regular prices, not promotional
      );
      return {
        ...op,
        fromPrice: Math.min(
          ...op.plans.map((plan) => plan.promotion?.price ?? plan.price)
        ),
        lowestRegularPrice,
      };
    })
    .sort((a, b) => a.lowestRegularPrice - b.lowestRegularPrice);

  const currentOperator = operatorsWithFromPrice.find(
    (op) => op.skuMatcher === selectedOperator
  );

  return (
    <div className="w-full min-w-0 space-y-5">
      {/* Single container for all options including the static one */}
      <div className="relative w-full bg-gray-100 rounded-6 p-1.5 overflow-x-auto flex items-center space-x-3">
        {/* Static "Only Device" option */}
        <button
          onClick={() => handleOperatorChange(null)}
          className={`flex flex-col items-center justify-center py-3 px-4 max-w-[140px] rounded-full transition-colors 
            ${
              !selectedOperator || selectedOperator === "only-phone"
                ? "bg-white"
                : "bg-transparent hover:bg-white"
            }`}
        >
          <span className="font-semibold text-sm">
            {t("operators.staticOptionOnlyDevice")}
          </span>
        </button>

        {/* Operators row */}
        {operatorsWithFromPrice.map((operator) => {
          const isSelected = selectedOperator === operator.skuMatcher;
          return (
            <button
              key={operator.skuMatcher}
              onClick={() => handleOperatorChange(operator.skuMatcher)}
              className={`flex flex-col items-center justify-center py-2 px-4 min-w-[140px] rounded-full transition-colors 
              ${
                isSelected
                  ? "bg-white border-primary"
                  : "bg-transparent hover:bg-white"
              }`}
            >
              <img
                src={urlFor(operator.logoRef).url()}
                alt={operator.name}
                className="h-8 w-16 object-contain py-1"
              />
              <span className="text-xs font-semibold text-gray-800">
                {t("operators.operatorTabPrice", {
                  price: operator.fromPrice,
                })}
              </span>
            </button>
          );
        })}
      </div>

      {/* Plans section */}
      {selectedOperator && ( // Changed condition to only show when an actual operator is selected
        <div className="mt-5">
          {currentOperator?.plans.map((plan, index) => (
            <PlanItem
              key={plan.sku}
              plan={plan}
              operatorIcon={urlFor(currentOperator.iconRef).url()}
              isSelected={selectedPlan === plan.sku}
              onSelect={handlePlanSelect}
              index={index}
              totalPlans={currentOperator.plans.length}
              showAllPlans={selectedPlan === null}
            />
          ))}
          {/* Rest of the code remains the same */}
        </div>
      )}

      {/* TODO: Needs to be fixed with real links */}
      {currentOperator && (
        <p className="text-gray-600 text-xs text-center mt-5 px-6">
          {t("operators.termsTextBefore", {
            operatorName: currentOperator.name,
          })}
          <a
            className="underline"
            href={currentOperator.termsAndConditions}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("operators.termsTextLink", {
              operatorName: currentOperator.name,
            })}
          </a>
        </p>
      )}

      {selectedPlan && (
        <div className="mt-5 w-full min-w-0">
          <PhoneNumberSelection />
        </div>
      )}
    </div>
  );
};
